import type { FC } from 'react'

import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface TrajectoryArcProps {
    className?: string
}

const PATH = 'M 24 252 Q 230 -44 436 252'

/** Ball positions at equal time steps, like a multi-flash photograph of a throw. */
const STROBE = [
    { x: 65.2, y: 198.72 },
    { x: 106.4, y: 157.28 },
    { x: 147.6, y: 127.68 },
    { x: 188.8, y: 109.92 },
    { x: 230, y: 104 },
    { x: 271.2, y: 109.92 },
    { x: 312.4, y: 127.68 },
    { x: 353.6, y: 157.28 },
    { x: 394.8, y: 198.72 }
]

/** Share of the path length covered at each tenth of the flight, so the ball slows at the apex like a real throw. */
const KEY_POINTS = '0;0.1276;0.2385;0.3349;0.4204;0.5;0.5796;0.6651;0.7615;0.8724;1'
const KEY_TIMES = '0;0.1;0.2;0.3;0.4;0.5;0.6;0.7;0.8;0.9;1'

const TrajectoryArc: FC<TrajectoryArcProps> = ({ className }) => {
    const reduced = useReducedMotion()

    return (
        <svg viewBox='0 0 460 284' fill='none' aria-hidden='true' className={className}>
            <path d='M 0 252 H 460' stroke='rgba(244, 242, 238, 0.1)' />

            {STROBE.map(point => (
                <path key={point.x} d={`M ${point.x} 248 V 256`} stroke='rgba(244, 242, 238, 0.16)' />
            ))}

            <path d={PATH} stroke='rgba(244, 242, 238, 0.2)' strokeDasharray='2 7' />
            <path d='M 230 112 V 252' stroke='rgba(244, 242, 238, 0.1)' strokeDasharray='2 5' />

            {STROBE.map((point, index) => (
                <circle
                    key={point.x}
                    cx={point.x}
                    cy={point.y}
                    r='4.5'
                    stroke='rgba(244, 242, 238, 0.28)'
                    strokeOpacity={0.35 + (index / STROBE.length) * 0.5}
                />
            ))}

            <path d='M 24 252 L 64 190' stroke='rgba(255, 91, 31, 0.75)' strokeWidth='1.25' />
            <path d='M 64 190 L 56.5 193.5 M 64 190 L 63.4 198.3' stroke='rgba(255, 91, 31, 0.75)' strokeWidth='1.25' strokeLinecap='round' />

            <g className='fill-steel font-mono' fontSize='10' letterSpacing='0.06em'>
                <text x='70' y='186'>v₀</text>
                <text x='242' y='96'>apex, vᵧ = 0</text>
                <text x='290' y='232'>y(t) = v₀t − ½gt²</text>
                <text x='18' y='274'>t₀</text>
                <text x='428' y='274'>t₁</text>
                <text x='196' y='274'>Δx constant</text>
            </g>

            {reduced
                ? <circle cx='230' cy='104' r='5.5' fill='#FF5B1F' />
                : (
                    <circle r='5.5' fill='#FF5B1F'>
                        <animateMotion
                            dur='3.4s'
                            repeatCount='indefinite'
                            path={PATH}
                            keyPoints={KEY_POINTS}
                            keyTimes={KEY_TIMES}
                            calcMode='linear'
                        />
                    </circle>
                )}
        </svg>
    )
}

export default TrajectoryArc
